import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Package, Phone, Briefcase, Home as HomeIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../firebase/auth';

// --- Constants ---
// We use a higher breakpoint to ensure tablets get the hamburger menu
// effectively preventing the nav links from crashing into the logo.
const MOBILE_BREAKPOINT = '1024px';

// --- Styled Components ---

const HeaderContainer = styled.header`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  z-index: 1000;
  transition: all 0.3s ease;
  box-shadow: ${props => props.$scrolled ? '0 4px 20px -5px rgba(0,0,0,0.1)' : 'none'};
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  text-decoration: none;
  letter-spacing: -0.03em;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 1001; /* Ensure logo stays above mobile menu if needed */

  span {
    color: #6366f1;
  }
`;

// --- Desktop Only Elements ---

const DesktopLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  /* Hides on Tablet/Mobile */
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 8px 0;
  position: relative;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #0f172a;
  }

  &.active {
    color: #6366f1;
    font-weight: 600;
  }

  &.active::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 4px;
    background-color: #6366f1;
    border-radius: 50%;
  }
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  /* Hides on Tablet/Mobile */
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || MOBILE_BREAKPOINT}) {
    display: none;
  }
`;

const LoginButton = styled.button`
  background-color: #0f172a;
  color: white;
  padding: 10px 24px;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #334155;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
  }
`;

/* Profile Dropdown */
const ProfileContainer = styled.div`
  position: relative;
`;

const ProfileTrigger = styled.button`
  background: white;
  border: 1px solid #e2e8f0;
  padding: 6px 12px 6px 6px;
  border-radius: 50px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover, &.active {
    border-color: #cbd5e1;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  }

  .avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #6366f1, #818cf8);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 120%;
  right: 0;
  background: white;
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: 0 10px 40px -10px rgba(0,0,0,0.1);
  min-width: 200px;
  overflow: hidden;
  padding: 8px;
  z-index: 1001;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #475569;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f8fafc;
    color: #0f172a;
  }

  &.logout {
    color: #ef4444;
    &:hover {
      background-color: #fef2f2;
    }
  }
`;

// --- Mobile Only Elements ---

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: #0f172a;
  cursor: pointer;
  z-index: 1001;
  padding: 8px;

  /* Shows on Tablet/Mobile */
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || MOBILE_BREAKPOINT}) {
    display: block;
  }
`;

const MobileOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100vh;
  width: 100vw;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  z-index: 1000; /* Stacks below toggle button if you want, or handle close inside */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const MobileLink = styled(Link)`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  text-decoration: none;
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.95);
  }

  &.active {
    color: #6366f1;
  }
`;

// --- Component Logic ---

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const profileRef = useRef(null);

  // Scroll Detection
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Click Outside Profile Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset state on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [location]);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isActive = (path) => location.pathname === path ? 'active' : '';

  return (
    <HeaderContainer $scrolled={isScrolled}>
      <Nav>
        <Logo to="/">
          SDD <span>LOGISTICS</span>
        </Logo>

        {/* --- DESKTOP VIEW --- */}
        <DesktopLinks>
          <NavLink to="/" className={isActive('/')}>Home</NavLink>
          <NavLink to="/tracking" className={isActive('/tracking')}>Track Order</NavLink>

          <NavLink to="/contact" className={isActive('/contact')}>Contact</NavLink>
        </DesktopLinks>

        <AuthSection>
          {isAuthenticated ? (
            <ProfileContainer ref={profileRef}>
              <ProfileTrigger
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                $isOpen={isProfileOpen}
                className={isProfileOpen ? 'active' : ''}
              >
                <div className="avatar"><User size={18} /></div>
                <ChevronDown size={16} />
              </ProfileTrigger>
              <AnimatePresence>
                {isProfileOpen && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  >
                    <div style={{ padding: '8px 16px', fontSize: '0.85rem', color: '#94a3b8', fontWeight: 600 }}>Account</div>
                    <DropdownItem onClick={() => navigate('/dashboard')}>
                      <LayoutDashboard size={18} /> Dashboard
                    </DropdownItem>
                    <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 0' }} />
                    <DropdownItem onClick={handleLogout} className="logout">
                      <LogOut size={18} /> Logout
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </ProfileContainer>
          ) : (
            <LoginButton onClick={() => navigate('/login')}>
              <User size={18} /> Login
            </LoginButton>
          )}
        </AuthSection>

        {/* --- MOBILE VIEW --- */}
        <MobileToggle onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </MobileToggle>

        <AnimatePresence>
          {isMenuOpen && (
            <MobileOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Close button inside overlay for better UX */}
              <button
                onClick={() => setIsMenuOpen(false)}
                style={{ position: 'absolute', top: '24px', right: '24px', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={32} color="#0f172a" />
              </button>

              <MobileLink to="/" className={isActive('/')}>
                <HomeIcon size={24} /> Home
              </MobileLink>
              <MobileLink to="/tracking" className={isActive('/tracking')}>
                <Package size={24} /> Track Order
              </MobileLink>
              <MobileLink to="/careers" className={isActive('/careers')}>
                <Briefcase size={24} /> Careers
              </MobileLink>
              <MobileLink to="/contact" className={isActive('/contact')}>
                <Phone size={24} /> Contact
              </MobileLink>

              <div style={{ width: '60px', height: '2px', background: '#e2e8f0', margin: '30px 0' }} />

              {isAuthenticated ? (
                <>
                  <MobileLink to="/dashboard">
                    <LayoutDashboard size={24} /> Dashboard
                  </MobileLink>
                  <button
                    onClick={handleLogout}
                    style={{
                      background: 'none', border: 'none', fontSize: '1.5rem',
                      fontWeight: 600, color: '#ef4444', display: 'flex', gap: '12px', alignItems: 'center', marginTop: '16px'
                    }}
                  >
                    <LogOut size={24} /> Logout
                  </button>
                </>
              ) : (
                <LoginButton
                  onClick={() => navigate('/login')}
                  style={{ width: '200px', justifyContent: 'center', padding: '16px', fontSize: '1.1rem' }}
                >
                  <User size={20} /> Login
                </LoginButton>
              )}
            </MobileOverlay>
          )}
        </AnimatePresence>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;